import React, { useState, useEffect } from 'react';
import styles from './VideoPlayer.module.scss';

interface Comment {
  id: number;
  startFrame: number;
  endFrame: number;
  text: string;
  shapes?: any[];
  user?: {
    firstName: string;
    lastName: string;
  };
  isResolved?: boolean;
}

interface SeekBarProps {
  seekbarRef: React.RefObject<HTMLDivElement>;
  currentTime: number;
  duration: number;
  comments: Array<{ id: number; startFrame: number; endFrame: number; text: string; user?: { firstName: string; lastName: string; }; isResolved?: boolean }>;
  fps?: number;
  selectedCommentId?: number | null;
  onSeekbarClick: (e: React.MouseEvent<HTMLDivElement> | { clientX: number }) => void;
  onSeekbarDrag: (e: React.MouseEvent<HTMLDivElement> | { clientX: number }) => void;
  onCommentClick?: (commentId: number) => void;
  showResolved?: boolean;
}

const SeekBar: React.FC<SeekBarProps> = ({
  seekbarRef,
  currentTime,
  duration,
  comments,
  fps = 30,
  selectedCommentId,
  onSeekbarClick,
  onSeekbarDrag,
  onCommentClick,
  showResolved,
}) => {
  // Mouse sürükleme state'i
  const [isDragging, setIsDragging] = useState(false);

  // Mouse sürükleme başladığında
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    onSeekbarDrag(e);
  };

  // Mouse sürükleme bittiğinde
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Document seviyesinde mouse hareketi dinle
  useEffect(() => {
    if (isDragging) {
      const handleDocumentMouseMove = (e: MouseEvent) => {
        onSeekbarDrag({ clientX: e.clientX } as any);
      };

      const handleDocumentMouseUp = () => {
        setIsDragging(false);
      };

      document.addEventListener('mousemove', handleDocumentMouseMove);
      document.addEventListener('mouseup', handleDocumentMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleDocumentMouseMove);
        document.removeEventListener('mouseup', handleDocumentMouseUp);
      };
    }
  }, [isDragging, onSeekbarDrag]);

  // Resolved pinleri filtrele
  const visibleComments = (showResolved ? comments : comments.filter(c => !c.isResolved));

  return (
    <div
      ref={seekbarRef}
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        width: '100%',
        top: 0,
        height: 6,
        background: '#2a2a2a',
        borderRadius: 3,
        zIndex: 1000,
        cursor: 'pointer',
      }}
      onClick={onSeekbarClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onTouchStart={(e) => {
        e.preventDefault();
        const touch = e.touches[0];
        const rect = seekbarRef.current?.getBoundingClientRect();
        if (rect) {
          const x = touch.clientX - rect.left;
          const percent = Math.max(0, Math.min(1, x / rect.width));
          const time = percent * duration;
          onSeekbarDrag({ clientX: touch.clientX } as any);
        }
      }}
      onTouchMove={(e) => {
        e.preventDefault();
        const touch = e.touches[0];
        const rect = seekbarRef.current?.getBoundingClientRect();
        if (rect) {
          const x = touch.clientX - rect.left;
          const percent = Math.max(0, Math.min(1, x / rect.width));
          const time = percent * duration;
          onSeekbarDrag({ clientX: touch.clientX } as any);
        }
      }}
      onTouchEnd={(e) => {
        e.preventDefault();
        // Touch bittiğinde range slider'ı göster
        const touch = e.changedTouches[0];
        const rect = seekbarRef.current?.getBoundingClientRect();
        if (rect) {
          const x = touch.clientX - rect.left;
          const percent = Math.max(0, Math.min(1, x / rect.width));
          const time = percent * duration;
          onSeekbarClick({ clientX: touch.clientX } as any);
        }
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          height: 6,
          width: `${(currentTime / duration) * 100}%`,
          background: '#7c3aed',
          borderRadius: 3,
        }}
      />
      
      {/* Comment pinleri */}
      {visibleComments.map((comment) => {
        const commentStartTime = comment.startFrame / fps;
        const commentEndTime = comment.endFrame / fps;
        const commentStartPosition = (commentStartTime / duration) * 100;
        const commentEndPosition = (commentEndTime / duration) * 100;
        
        // Range farkı 1'den büyükse çizgi göster
        const showRange = (comment.endFrame - comment.startFrame) > 1;
        const isSelected = selectedCommentId === comment.id;
        
        return (
          <div key={comment.id}>
            {/* Pin */}
            <div
              style={{
                position: 'absolute',
                left: `${commentStartPosition}%`,
                top: -16,
                width: isSelected ? 12 : 8,
                height: isSelected ? 12 : 8,
                background: isSelected ? 'var(--app-accent-color)' : 'var(--app-accent-color)',
                borderRadius: '50%',
                border: isSelected ? '3px solid #fff' : '2px solid #fff',
                cursor: 'pointer',
                zIndex: 3,
                transform: 'translateX(-50%)',
                boxShadow: isSelected ? '0 4px 12px rgba(0,194,113,0.6)' : '0 2px 4px rgba(0,0,0,0.3)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateX(-50%) scale(1.3)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateX(-50%) scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
              }}
              onClick={(e) => {
                e.stopPropagation();
                onCommentClick?.(comment.id);
              }}
              title={`${comment.user?.firstName || 'User'}: ${comment.text.substring(0, 50)}${comment.text.length > 50 ? '...' : ''}`}
            />
            
            {/* Range çizgisi - sadece fark 1'den büyükse göster */}
            {showRange && (
              <div
                style={{
                  position: 'absolute',
                  left: `${commentStartPosition}%`,
                  top: -12,
                  width: `${Math.max(commentEndPosition - commentStartPosition, 0.1)}%`,
                  height: 2,
                  background: 'var(--app-accent-color)',
                  zIndex: 2,
                  borderRadius: 1,
                  opacity: 0.7,
                  pointerEvents: 'none',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SeekBar; 